import { ImagerResizer } from "components/Form/components/ImageUpload/ImagerResizer";
import { Input } from "components/Form/components/Input/Input";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { IconPicker } from "components/Icon/IconPicker";
import { parseIconValue, resolveCuratedIcon } from "components/Icon/iconValue";
import { Modal } from "components/Modal/Modal";
import { Popover } from "components/Popover/Popover";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import "./ImageUpload.scss";

export const ImageUpload = ({ input, meta, fieldProps, fieldAttr }) => {
    const inputRef = useRef(null);
    const [isPickerOpen, setPickerOpen] = useState(false);

    const maxImageSize = useWorkspaceConfigValue("ICON_MAX_SIZE_PX") ?? 512;

    // Opt-in per resource (via fieldProps): show the curated built-in-icon
    // picker alongside upload. Gated because a curated `mdi:<key>` value only
    // renders correctly where the resource's icon render paths understand it.
    const enableCuratedPicker = !!fieldProps?.enableCuratedPicker;

    const parsed = parseIconValue(input.value);
    const curatedIcon = parsed.kind === "curated" ? resolveCuratedIcon(input.value) : undefined;
    const isImage = parsed.kind === "image";

    const onAdd = (files) => {
        const file = files?.target?.files[0];
        const reader = new FileReader();
        reader.addEventListener("load", async () => {
            const resizedImage = await ImagerResizer(reader.result, maxImageSize, "both");

            input.onChange(resizedImage);
        });

        if (file) {
            reader.readAsDataURL(file);
        }
    };

    const onRemove = () => {
        input.onChange(null);
        if (inputRef.current) {
            inputRef.current.value = null;
        }
    };

    const onPickCurated = (curatedValue) => {
        input.onChange(curatedValue);
        setPickerOpen(false);
    };

    return (
        <Input fieldProps={fieldProps} fieldAttr={fieldAttr} input={input} meta={meta}>
            <Popover
                trigger={
                    <div className="image-upload">
                        {parsed.kind === "none" && (
                            <Icon className="image-upload__icon" icon={IconCollection.image} />
                        )}
                        {curatedIcon && (
                            <Icon className="image-upload__curated" icon={curatedIcon} />
                        )}
                        <div
                            className={`image-upload__label${
                                isImage ? " image-upload__label--has-image" : ""
                            }`}
                            style={{ backgroundImage: isImage ? `url(${input.value})` : "" }}
                        />
                        <input
                            ref={inputRef}
                            id={input.name}
                            onChange={onAdd}
                            aria-label={`${input.name}`}
                            type="file"
                            accept="image/*"
                        />
                    </div>
                }
                anchorOrigin={{ vertical: 45, horizontal: "left" }}
            >
                <ul className="popoverMenu">
                    <li>
                        <button type="button" onClick={() => inputRef.current.click()}>
                            <Icon icon={IconCollection.upload} />
                            Upload
                        </button>
                    </li>
                    {enableCuratedPicker && (
                        <li>
                            <button type="button" onClick={() => setPickerOpen(true)}>
                                <Icon icon={IconCollection.image} />
                                Choose icon
                            </button>
                        </li>
                    )}
                    {!!input.value && (
                        <li>
                            <button type="button" onClick={onRemove}>
                                <Icon icon={IconCollection.deleteOutline} />
                                Remove
                            </button>
                        </li>
                    )}
                </ul>
            </Popover>
            {enableCuratedPicker && (
                <Modal
                    isVisible={isPickerOpen}
                    onCancel={() => setPickerOpen(false)}
                    title="Choose an icon"
                    destroyOnClose
                >
                    <IconPicker value={input.value} onSelect={onPickCurated} />
                </Modal>
            )}
        </Input>
    );
};

ImageUpload.defaultProps = {
    fieldProps: {},
    fieldAttr: {},
    input: {},
    meta: {},
};
ImageUpload.propTypes = {
    fieldProps: PropTypes.oneOfType([PropTypes.object]),
    fieldAttr: PropTypes.oneOfType([PropTypes.object]),
    input: PropTypes.oneOfType([PropTypes.object]),
    meta: PropTypes.oneOfType([PropTypes.object]),
};
